import ConvertIds from '@atlas/atlas-convert-ids';
import OnDemandData from '@atlas/on-demand-data';
import propCache from '@atlas/prop-cache';
import { PMI, PickingMode } from '@pvw/plmvisweb';
import {
    SELECTION_REASONS,
    getChildComponentSet,
    getObjectTypeByPsId,
    getParentComponents,
    hasChildComponent,
    isCurveFromPsId,
    toPsId
} from '../atlasSelectionUtils';
import { getSecondarySelectionMaterial } from '../secondarySelectionColor';
/**
 *
 * @param {string} psId - PlmVisWeb psId
 * @returns {object} - objects in PlmVisWeb viewer
 */
const getObjectByPsId = (psId) => {
    let ret = viewer._modelObject.getObjectByPsId(psId);
    if (!ret) {
        //TODO: Every moment I live is torture
        if (PMI && viewer.pmi) {
            var pmiMgr = viewer.pmi;
            if (pmiMgr) {
                ret = pmiMgr._pmiObject.getObjectByPsId(psId);
            }
        }
    }
    return ret;
};

/**
 *
 * @param {string} psId - PlmVisWeb psId
 * @param {boolean} isEdgeId
 * @returns {number[]} - point location
 */
const getPointByEdgeId = (psId, isEdgeId = true) => {
    try {
        if (!isEdgeId) {
            let edges = viewer.getEdgeIdsById(psId);
            if (edges !== undefined && typeof edges[0] === 'string' && edges[0].length > 0) {
                psId = edges[0];
            } else {
                return;
            }
        }
        let points = viewer.getEdgeGeometryByPsId(psId);
        let middle = [];
        if (points.length === 6) {
            // linear edge
            for (let i = 0; i < 3; i++) {
                middle.push((points[i] + points[i + 3]) / 2);
            }
        } else if (points.length > 6) {
            // non linear edge
            const middlePoint = Math.floor(points.length / 2);
            middle = points.slice(middlePoint, middlePoint + 3);
        }
        return middle;
    } catch (error) {
        // do nothing
    }
};

/**
 * Represents a selection object, this class only contains essential properties for atlas-selection,
 * clients can extend this class to add client specific functionalities.
 */
export class SelectionObject {
    /**
     * @param {Array | string} id - In general, id is the atlasId(Array) of object but some SelectionElement use string as id
     * @param {string[]} psIds - psIds of geometries that represent this selected object
     * @param {string} type - type of this selection object
     * @param {string} displayName - display name(localization is not supported yet?)
     * @param {SelectionElement} sourceElement - Link back to the SelectionElement that created this SelectionObject
     */
    constructor(id, psIds, type, displayName, sourceElement) {
        /** @type {Array | string}  */
        this.id = id;
        /** @type {string[]}  */
        this.psIds = psIds || [];
        /** @type {string}  */
        this.type = type;
        /** @type {string}  */
        this.displayName = displayName || '';
        /**
         *  @type {SelectionElement}
         *  @private
         */
        // TODO: set sourceElement by setter, sanppoint may create a selectionObject without sourceElement now
        this._sourceElement = sourceElement;
        /** @type {boolean} */
        this.nonGraphicSelect = false;
        /** @type {number[]} */
        this.modelPoint = undefined;
        /**
         * @type {object}
         * Optional, some selection element(e.g. SanpPoint, Feature Element) need it.
         */
        this.recipe = undefined;
        /** @type {boolean} */
        this.cancelAccepted = false;
        /**
         * @type {boolean}
         * Is blocked by an opaque face
         */
        this.isBlocked = false;
        /**
         * @type {string}
         * Optional, some selection objects are deduced from other type objects(e.g. select body while picking on a face)
         * this is the psid of original object.
         */
        this.underCursorPsId = '';
    }

    /**
     * @returns {Array | string}
     */
    get id() {
        return this._id;
    }
    /**
     * @param {Array | string} id
     */
    set id(id) {
        if (Array.isArray(id) || (typeof id === 'string' && id.length > 0)) {
            this._id = id;
        } else {
            throw new Error('id must be an Array or string!');
        }
    }

    /**
     * @returns {string}
     */
    get type() {
        return this._type;
    }
    /**
     * @param {string} type
     */
    set type(type) {
        if (typeof type === 'string' && type.length > 0) {
            this._type = type;
        } else {
            throw new Error('type must be a valid string!');
        }
    }

    /**
     * @returns {SelectionElement}
     */
    get sourceElement() {
        return this._sourceElement;
    }
    /**
     * @param {SelectionElement} selEle
     */
    set sourceElement(selEle) {
        if (selEle instanceof SelectionElement) {
            this._sourceElement = selEle;
        } else {
            throw new Error('sourceElement must be an instance of SelectionElement!');
        }
    }
}

var viewer = null;
export class SelectionElement {
    /**
     * @param {any} viewerInp
     */
    constructor(viewerInp) {
        viewer = viewerInp;
        /**
         * @type {boolean}
         * @private
         */
        this._active = false;
        this._isStateful = true;
    }

    /**
     * @returns {boolean}
     */
    get active() {
        return this._active;
    }
    /**
     * @param {boolean} value
     */
    set active(value) {
        this._active = value;
    }

    /**
     * @returns {boolean} - true for in Stateful mode, false otherwise
     */
    get isStateful() {
        return this._isStateful;
    }

    /**
     * @param {boolean} value - true for Stateful mode, false otherwise
     */
    set isStateful(value) {
        this._isStateful = value;
    }

    /**
     * This method is called when the selection element's owning context is popped from the context stack
     * in other words, the context is losing selection focus.
     */
    onPop() {}

    /**
     *
     * @param {MouseEvent} e
     * @param {object[]} curObjects return value of viewer.getObjectIdsAtViewCoordinate
     * @returns {Promise<any>?}
     */
    async onMouseMove(e, curObjects) {
        return null;
    }

    /**
     * selectionObject will be added to context selection list
     *
     * @param {import('../Context').Context} context
     * @param {SelectionObject} selectionObject
     */
    onAddToSelected(context, selectionObject) {}

    /**
     * selectionObject will be removed from context
     *
     * @param {import('../Context').Context} context
     * @param {SelectionObject} selectionObject
     */
    onRemoveFromSelected(context, selectionObject) {}

    /**
     *
     * @param {boolean} [areaSelect] - is area select
     * @returns {number} - PlmVisWeb picking mask
     */
    getPickingMask(areaSelect) {
        throw new Error('getPickingMask not implemented.');
    }

    /**
     * Highlights selectionObject contents.  SelectionElements can have
     *    unique highlight behavior, see SnapPointElement for example
     *
     * @param {SelectionObject} selectionObject - Object to highlight
     * @param {boolean} [highlight] - true:  highlight object
     *                              false: dehighlight object
     * @returns {Array} Returns array of psIds to highlight in PLMVisWeb
     */
    highlight(selectionObject, highlight) {
        return selectionObject.psIds;
    }
    /**
     * Select selectionObject. SelectionElements can have
     *    unique selection behavior, see SnapPointElement for example
     *
     * @param {SelectionObject} selectionObject - Object to select
     * @param {boolean} select - true:  select object
     *                           false: deselect object
     * @param {import('../atlasSelectionUtils').SELECTION_REASONS} reason - reason for select defined in SELECTION_REASONS in atlasSelectionUtils.js
     *                  AtlasSelection always sends "reason" and the implementation can choose to use or ignore it.
     * @returns {Array} Returns array of psIds to select in PLMVisWeb, which will
     *                  highlight the psIds in the selection color.
     */
    select(selectionObject, select, reason) {
        return selectionObject.psIds;
    }
    /**
     * SecondaryHighlight selectionObject. SelectionElements can have
     *    unique selection behavior, see SnapPointElement for example
     *
     * @param {SelectionObject} selectionObject - Object to select
     * @param {boolean} secondaryHighlight - true: add secondary Highlight color to object
     *                           false:  cancel secondary highlight on object
     * @returns {Array} Returns array of psIds to secondaryHighlight in PLMVisWeb, which will
     *                  highlight the psIds in the secondaryHighlight color.
     */
    secondaryHighlight(selectionObject, secondaryHighlight) {
        return this.select(selectionObject, false, SELECTION_REASONS.FOCUS_OUT);
    }
    /**
     *
     * @param {string} psId - PlmVisWeb psId
     * @param {boolean} [areaSelect] - is area select
     * @returns {Promise<SelectionObject | SelectionObject[]>} Return a SelectionObject instance or array if psId is current type
     */
    async ofObject(psId, areaSelect) {
        return undefined;
    }

    /**
     * @param {string} modelId - PlmVisWeb modelId
     * @param {string[]} atlasId - Atlas Id
     * @param {object} additionalData - additional data needed to get the selection object if apply
     * @returns {SelectionObject} Return a SelectionObject instance if psId is current type
     */
    getSelectionObject(modelId, atlasId, additionalData = undefined) {
        return undefined;
    }

    /**
     * @param {string[]} psIds - PlmVisWeb psId
     * @param {boolean} partial - the state of partial area select
     * @returns {Promise<SelectionObject[]>} Selection objects array
     */
    objectsInArea(psIds, partial) {
        return Promise.resolve([]);
    }

    /**
     * @param {string[]} psIds - PlmVisWeb psIds
     * @param {SelectionElement} elem - selection element
     * @param {boolean} partial - is partial
     * @returns {Promise<SelectionObject[]>}
     */
    async getObjectIdsInArea(psIds, elem, partial) {
        let promiseArray = [];
        for (let id of psIds) {
            promiseArray.push(elem.ofObject(id, true));
        }
        let result = [];
        let ofObjectResults = await Promise.all(promiseArray);
        for (let i = 0; i < ofObjectResults.length; i++) {
            let ofObjectResult = ofObjectResults[i];
            if (ofObjectResult) {
                if (Array.isArray(ofObjectResult)) {
                    ofObjectResult.forEach((obj) => {
                        obj.nonGraphicSelect = true;
                        obj.sourceElement = elem;
                    });
                    result.push(...ofObjectResult);
                } else {
                    ofObjectResult.nonGraphicSelect = true;
                    ofObjectResult.sourceElement = elem;
                    result.push(ofObjectResult);
                }
            }
        }
        return result;
    }
    /**
     * Get the display icon's iconId of the SelectionElement. Child class of SelectionElement can have different implementation.
     *
     * @param {SelectionObject} [selectionObject] - the selectionObject, it's optional. For some selectionElement it need to learn from the specific selectionObject to get the iconId.
     * See FeatureElement for example.
     * @returns {string} Returns a string as the display icon's iconId of the specific SelectionElement.
     */
    getDisplayIcon(selectionObject) {
        return '';
    }

    /**
     * Gets the name of the selection element. Child class of SelectionElement can override the implementation
     *
     * @param {Array} [atlasId] - the optional atlas id of the selection object. atlasId is used to differentiate the
     *                        datum csys datum object from the datum object
     * @returns {string} the display name of the selection object
     */
    getDisplayName(atlasId) {
        return '';
    }
}

export class EdgeElement extends SelectionElement {
    /**
     * @param {string} psId
     */
    async ofObject(psId) {
        let result = undefined;
        if (getObjectTypeByPsId(viewer, psId) === 'Edge') {
            result = new SelectionObject(ConvertIds.fromPsId(viewer, psId), [psId], 'Edge', this.getDisplayName(), this);
            let midPoint = getPointByEdgeId(psId);
            if (midPoint !== undefined) {
                result.modelPoint = midPoint;
            }
        }
        return result;
    }
    getPickingMask() {
        return PickingMode.EDGE;
    }

    /**
     * @param {string} modelId
     * @param {any[]} atlasId
     */
    getSelectionObject(modelId, atlasId) {
        return new SelectionObject(atlasId, [ConvertIds.toPsId(modelId, atlasId)], 'Edge', this.getDisplayName(), this);
    }

    /**
     * @param {string[]} psIds - PlmVisWeb psId
     * @param {boolean} partial
     */
    objectsInArea(psIds, partial) {
        return super.getObjectIdsInArea(psIds, this, partial);
    }

    getDisplayName() {
        return 'Edge';
    }
    getDisplayIcon() {
        return 'select_edge';
    }
}

export class CurveElement extends SelectionElement {
    /**
     * @inheritdoc
     * @param {string} psId
     */
    async ofObject(psId) {
        let result = undefined;
        let type = getObjectTypeByPsId(viewer, psId);
        if (isCurveFromPsId(viewer, psId)) {
            result = new SelectionObject(ConvertIds.fromPsId(viewer, psId), [psId], type, this.getDisplayName(), this);
        }
        return result;
    }

    getPickingMask() {
        return PickingMode.DISPLAY_ENTITY;
    }

    /**
     * @inheritdoc
     * @param {string} modelId
     * @param {any[]} atlasId
     */
    getSelectionObject(modelId, atlasId) {
        let psId = ConvertIds.toPsId(modelId, atlasId);
        let type = getObjectTypeByPsId(viewer, psId);
        return new SelectionObject(atlasId, [psId], type, this.getDisplayName(), this);
    }

    /**
     * @param {string[]} psIds - PlmVisWeb psId
     * @param {boolean} partial
     */
    objectsInArea(psIds, partial) {
        return super.getObjectIdsInArea(psIds, this, partial);
    }

    getDisplayName() {
        return 'Curve';
    }
    getDisplayIcon() {
        return 'selectCurve24';
    }
}

export class PointElement extends SelectionElement {
    /**
     * @inheritdoc
     * @param {string} psId
     */
    async ofObject(psId) {
        let result = undefined;
        let type = getObjectTypeByPsId(viewer, psId);
        if (type === 'Point') {
            result = new SelectionObject(ConvertIds.fromPsId(viewer, psId), [psId], type, this.getDisplayName(), this);
        }
        return result;
    }

    getPickingMask() {
        return PickingMode.DISPLAY_ENTITY;
    }

    /**
     * @inheritdoc
     * @param {string} modelId
     * @param {any[]} atlasId
     */
    getSelectionObject(modelId, atlasId) {
        let psId = ConvertIds.toPsId(modelId, atlasId);
        let type = getObjectTypeByPsId(viewer, psId);
        return new SelectionObject(atlasId, [psId], type, this.getDisplayName(), this);
    }

    /**
     * @param {string[]} psIds - PlmVisWeb psId
     * @param {boolean} partial
     */
    objectsInArea(psIds, partial) {
        return super.getObjectIdsInArea(psIds, this, partial);
    }

    getDisplayName() {
        return 'Point';
    }
    getDisplayIcon() {
        return 'selectPoint24';
    }
}

export class DatumPlaneElement extends SelectionElement {
    /**
     * @inheritdoc
     * @param {string} psId
     */
    async ofObject(psId) {
        let result = undefined;
        let type = getObjectTypeByPsId(viewer, psId);
        if (type === 'Datum Plane') {
            result = new SelectionObject(ConvertIds.fromPsId(viewer, psId), [psId], type, this.getDisplayName(), this);
        }
        return result;
    }

    getPickingMask() {
        return PickingMode.DISPLAY_ENTITY;
    }

    /**
     * @inheritdoc
     * @param {string} modelId
     * @param {any[]} atlasId
     */
    getSelectionObject(modelId, atlasId) {
        let psId = ConvertIds.toPsId(modelId, atlasId);
        return new SelectionObject(atlasId, [psId], this.getDisplayName(), this.getDisplayName(), this);
    }

    /**
     * @param {string[]} psIds - PlmVisWeb psId
     * @param {boolean} partial
     */
    objectsInArea(psIds, partial) {
        return super.getObjectIdsInArea(psIds, this, partial);
    }

    getDisplayName() {
        return 'Datum Plane';
    }
    getDisplayIcon() {
        return 'selectDatumPlane32';
    }
}

export class DatumAxisElement extends SelectionElement {
    /**
     * @inheritdoc
     * @param {string} psId
     */
    async ofObject(psId) {
        let result = undefined;
        let type = getObjectTypeByPsId(viewer, psId);
        if (type === 'Datum Axis') {
            result = new SelectionObject(ConvertIds.fromPsId(viewer, psId), [psId], type, this.getDisplayName(), this);
        }
        return result;
    }

    getPickingMask() {
        return PickingMode.DISPLAY_ENTITY;
    }

    /**
     * @inheritdoc
     * @param {string} modelId
     * @param {any[]} atlasId
     */
    getSelectionObject(modelId, atlasId) {
        let psId = ConvertIds.toPsId(modelId, atlasId);
        return new SelectionObject(atlasId, [psId], this.getDisplayName(), this.getDisplayName(), this);
    }

    /**
     * @inheritdoc
     * @param {string[]} psIds
     * @param {boolean} partial
     */
    objectsInArea(psIds, partial) {
        return super.getObjectIdsInArea(psIds, this, partial);
    }

    getDisplayName() {
        return 'Datum Axis';
    }
    getDisplayIcon() {
        return 'datumaxis';
    }
}

export class PmiElement extends SelectionElement {
    secondaryHighlight(selectionObject, secondaryHighlight) {
        super.secondaryHighlight(selectionObject, secondaryHighlight);
        if (secondaryHighlight && getSecondarySelectionMaterial()) {
            viewer.pmi.setMaterialByPsId(selectionObject.psIds, getSecondarySelectionMaterial());
        } else {
            viewer.pmi.resetMaterialByPsId(selectionObject.psIds);
        }
        return [];
    }
    /**
     * @param {string} psId
     */
    async ofObject(psId) {
        let result = undefined;
        if (getObjectTypeByPsId(viewer, psId) === 'PMI') {
            result = new SelectionObject(ConvertIds.fromPsId(viewer, psId), [psId], 'PMI', this.getDisplayName(), this);
        }
        return result;
    }
    getPickingMask() {
        return PickingMode.PMI;
    }

    /**
     * @inheritdoc
     * @param {string} modelId
     * @param {any[]} atlasId
     */
    getSelectionObject(modelId, atlasId) {
        return new SelectionObject(atlasId, [ConvertIds.toPsId(modelId, atlasId)], 'PMI', this.getDisplayName(), this);
    }

    /**
     * @param {string[]} psIds - PlmVisWeb psId
     * @param {boolean} partial
     */
    objectsInArea(psIds, partial) {
        return super.getObjectIdsInArea(psIds, this, partial);
    }

    getDisplayName() {
        return 'PMI';
    }
    getDisplayIcon() {
        return 'pmi';
    }
}

export class BodyElement extends SelectionElement {
    /**
     * @inheritdoc
     * @param {string} psId
     */
    async ofObject(psId, areaSelect = false) {
        let result = undefined;
        if (areaSelect && getObjectTypeByPsId(viewer, psId) === 'Body') {
            result = new SelectionObject(ConvertIds.fromPsId(viewer, psId), [psId], 'Body', this.getDisplayName(), this);
            let midPoint = getPointByEdgeId(psId, false);
            if (midPoint !== undefined) {
                result.modelPoint = midPoint;
            }
        } else if (!areaSelect && getObjectTypeByPsId(viewer, psId) === 'Face') {
            let bodPsIds = viewer.getBodyIdsById(psId);
            if (bodPsIds && bodPsIds.length > 0) {
                result = new SelectionObject(ConvertIds.fromPsId(viewer, bodPsIds[0]), [bodPsIds[0]], 'Body', this.getDisplayName(), this);
                result.underCursorPsId = psId;
                let midPoint = getPointByEdgeId(psId, false);
                if (midPoint !== undefined) {
                    result.modelPoint = midPoint;
                }
            }
        }
        return result;
    }
    getPickingMask(areaSelect = false) {
        if (areaSelect) {
            return PickingMode.BODY;
        }
        return PickingMode.FACE;
    }

    /**
     * @inheritdoc
     * @param {string} modelId
     * @param {any[]} atlasId
     */
    getSelectionObject(modelId, atlasId) {
        return new SelectionObject(atlasId, [ConvertIds.toPsId(modelId, atlasId)], 'Body', this.getDisplayName(), this);
    }

    /**
     * @param {string[]} psIds - PlmVisWeb psId
     * @param {boolean} partial
     */
    objectsInArea(psIds, partial) {
        return super.getObjectIdsInArea(psIds, this, partial);
    }

    getDisplayName() {
        return 'Body';
    }
    getDisplayIcon() {
        return 'select_body';
    }
}

export class PartElement extends SelectionElement {
    /**
     * @inheritdoc
     * @param {string} psId
     */
    async ofObject(psId, areaSelect = false) {
        let result = undefined;
        let type = getObjectTypeByPsId(viewer, psId);
        if (areaSelect && type === 'Part') {
            result = this.createSelectionObject(ConvertIds.fromPsId(viewer, psId), !areaSelect);
            let midPoint = getPointByEdgeId(psId, false);
            if (midPoint !== undefined) {
                result.modelPoint = midPoint;
            }
        } else if (!areaSelect && type !== 'Dimension') {
            let partPsIds = viewer.getPartIdsById(psId);
            if (partPsIds && partPsIds.length > 0) {
                // viewer.getPartIdsById has a regression in 1.7.3-240 as compared to 1.7.3-165. This function can
                // return null in some cases. The case found here can be duplicated by either trying to select a part
                // or deselect a face in measurement. The following issue is created:
                // https://gitlab.industrysoftware.automation.siemens.com/PLMVisWeb/InternalCustomerCenter/-/issues/871
                const partPsId = partPsIds.pop();
                if (partPsId) {
                    const partAtlasId = ConvertIds.fromPsId(viewer, partPsId);
                    const parentComps = getParentComponents(viewer, JSON.stringify(partAtlasId));
                    if (parentComps?.size > 0) {
                        result = [];
                        const partSelObj = this.createSelectionObject(partAtlasId, !areaSelect);
                        if (type === 'Face') {
                            partSelObj.underCursorPsId = psId;
                        }
                        result.push(partSelObj);
                        parentComps.forEach((compStr) => {
                            const compAtlasId = JSON.parse(compStr);
                            const partSelObj1 = this.createSelectionObject(compAtlasId, !areaSelect);
                            if (type === 'Face') {
                                partSelObj1.underCursorPsId = psId;
                            }
                            result.push(partSelObj1);
                        });
                    } else {
                        result = this.createSelectionObject(partAtlasId, !areaSelect);
                        if (type === 'Face') {
                            result.underCursorPsId = psId;
                        }
                    }
                }
            }
        }
        return result;
    }
    getPickingMask(areaSelect = false) {
        if (areaSelect) {
            return PickingMode.PART;
        }
        return PickingMode.FACE | PickingMode.PMI | PickingMode.DISPLAY_ENTITY;
    }

    /**
     * @param {string} modelId
     * @param {any[]} atlasId
     * @returns {SelectionObject}
     */
    getSelectionObject(modelId, atlasId) {
        if (!Array.isArray(atlasId)) {
            return null;
        }

        // Creates a selection object for the input atlasId (part or asm), includes psId of all child comps
        let selectionObj = this.createSelectionObject(atlasId, true);
        return selectionObj;
    }

    /**
     * Creates a new selection object for an entity given its atlas id
     *
     * @param {Array} atlasId - the Atlas id of the entity (part of asm)
     * @param {boolean} [includeChildComps] - include psId of all child components
     * @returns {SelectionObject} a new selection object for the asm component
     */
    createSelectionObject(atlasId, includeChildComps = false) {
        let psIds = [];
        // If the object type is valid (invalid case - if asm without bodies/sketches etc), add the current id
        let psId = toPsId(viewer, atlasId);
        let objType = viewer.getObjectTypeByPsId(psId);
        if (objType === 'Part' || objType === 'Assembly') {
            psIds.push(psId);
        }

        if (includeChildComps) {
            // Assume getChildComponentSet will not return child component if atlasId is not a component type
            let childComps = getChildComponentSet(atlasId);
            if (childComps?.size) {
                childComps.forEach((compAtlasId) => {
                    let psId = toPsId(viewer, compAtlasId);
                    if (viewer.getObjectTypeByPsId(psId)) {
                        psIds.push(psId);
                    }
                });
            }
        }

        let displayName = this.getDisplayName(atlasId);
        return new SelectionObject(atlasId, psIds, 'Part', displayName, this);
    }

    /**
     * @param {string[]} psIds - PlmVisWeb psId
     * @param {boolean} partial
     */
    objectsInArea(psIds, partial) {
        return super.getObjectIdsInArea(psIds, this, partial);
    }

    /**
     * Gets the name of the selection element.
     *
     * @param {Array} [atlasId] - the optional atlas id of the selection object.
     * @returns {string} the display name of the selection object
     */
    getDisplayName(atlasId) {
        return atlasId && hasChildComponent(viewer, atlasId) ? 'Assembly' : 'Part';
    }

    /**
     * Get the display icon's iconId of the SelectionElement.
     *
     * @param {SelectionObject} [selectionObject] - the part selectionObject
     * @returns {string} Returns a string as the display icon's iconId of the part SelectionElement.
     */
    getDisplayIcon(selectionObject) {
        if (selectionObject.type === 'Part') {
            return hasChildComponent(viewer, /** @type {Array} */ (selectionObject.id)) ? 'appl_assemblies' : 'appl_modeling';
        }
        return '';
    }
}

export class DimensionElement extends SelectionElement {
    secondaryHighlight(selectionObject, secondaryHighlight) {
        super.secondaryHighlight(selectionObject, secondaryHighlight);
        if (secondaryHighlight && getSecondarySelectionMaterial()) {
            let color = getSecondarySelectionMaterial().diffuse;
            let colorObj = {
                text: color,
                extensionLine: color,
                extensionLineGap: color,
                dimensionLine: color
            };
            viewer.dimension.setDimensionColors(selectionObject.psIds[0], colorObj);
        } else {
            viewer.dimension.resetDimensionColors(selectionObject.psIds[0]);
        }
        return [];
    }
    /**
     * @inheritdoc
     * @param {string} psId
     */
    async ofObject(psId) {
        let result = undefined;
        if (getObjectTypeByPsId(viewer, psId) === 'Dimension') {
            result = new SelectionObject(psId, [psId], 'Dimension', this.getDisplayName(), this);
        }
        return result;
    }
    getPickingMask() {
        return PickingMode.DIMENSION;
    }

    /**
     * for all other elements, an atlasId is passed in.  Dimensions are client only, so I'll create
     * a selection object assuming the second parameter is really a psId.  Don't know how much
     * trouble this will cause.
     *
     * @param {any} modelId
     * @param {any} psId
     */
    getSelectionObject(modelId, psId) {
        return new SelectionObject(psId, [psId], 'Dimension', this.getDisplayName(), this);
    }

    /**
     * @param {string[]} psIds - PlmVisWeb psId
     * @param {boolean} partial
     */
    objectsInArea(psIds, partial) {
        return super.getObjectIdsInArea(psIds, this, partial);
    }

    getDisplayName() {
        return 'Dimension';
    }
    getDisplayIcon() {
        return 'selectDimension24';
    }

    set active(value) {
        super.active = value;
        viewer.dimension.setDimensionDragFlag(value);
    }

    get active() {
        return super.active;
    }

    onPop() {
        viewer.dimension.setDimensionDragFlag(false);
    }
}

let initialized = false;

export class FaceElement extends SelectionElement {
    /**
     * @param {any} viewerInp
     * @param {any} sendApi
     */
    constructor(viewerInp, sendApi) {
        super(viewerInp);
        this._faceTypes = [];
        this.sendApi = sendApi;
        this.requestCount = {};
    }
    get faceTypes() {
        return this._faceTypes;
    }
    set faceTypes(newFaceTypes) {
        if (!this.isStateful) {
            return;
        }

        if (!initialized && Array.isArray(newFaceTypes) && newFaceTypes.length > 0 && this._faceTypes.length === 0) {
            //make sure to register our type in the prop cache
            //unclear why ODD is passed in as input here
            //delegator should be obj with sendapi function
            propCache.initialize(viewer, this.sendApi);
            propCache.initializeOnDemandData(OnDemandData);
            propCache.register('SPLM.Face', ['SolidFaceType']);
            initialized = true;
        }
        // hmmm should clean ourselves up but don't know if anyone else is
        // using propCache... TODO
        /* else if (!newFaceTypes || Array.isArray(newFaceTypes) &&
        newFaceTypes.length === 0 && this._faceTypes.length > 0){
            propCache.uninitialize();
        } */
        this._faceTypes = newFaceTypes;
    }

    faceFilterOn() {
        return this._faceTypes && Array.isArray(this._faceTypes) && this._faceTypes.length > 0;
    }

    /**
     * @param {string[]} psIds - PlmVisWeb psIds
     * @param {boolean} partial
     */
    objectsInArea(psIds, partial) {
        return super.getObjectIdsInArea(psIds, this, partial);
    }

    /**
     * @param {any[]} atlasId
     * @param {string} psId
     * @returns {SelectionObject}
     */
    filtered(atlasId, psId) {
        let result = undefined;
        let candidate = new SelectionObject(atlasId, [psId], 'Face', this.getDisplayName(), this);
        if (this.faceFilterOn()) {
            //I don't want to create a new request if it's not there... just trust the cache
            const cachedProps = propCache.getCachedByIds([atlasId]);
            if (!cachedProps || cachedProps.length === 0 || !cachedProps[0].SolidFaceType) {
                //here I can request this face if it gets pinged enough.  I don't want to do it the first time
                // I get a request because I want to avoid random tiny objects the mouse might be over
                this.requestCount[psId] = this.requestCount[psId] === undefined ? 0 : this.requestCount[psId] + 1;
                //yes, exact comparison below.  Any less, we haven't gotten enough hits.  Any more, a request has already been issued
                //but we haven't gotten a response
                if (this.requestCount[psId] === 20) {
                    propCache.cacheByIds([atlasId]);
                }
            } else if (cachedProps[0] && cachedProps[0].SolidFaceType && this._faceTypes.includes(cachedProps[0].SolidFaceType)) {
                result = candidate;
            }
        } else {
            result = candidate;
        }
        return result;
    }
    /**
     * @inheritdoc
     * @param {string} psId
     */
    async ofObject(psId) {
        let type = getObjectTypeByPsId(viewer, psId);
        if (type === 'Face') {
            let atlasId = ConvertIds.fromPsId(viewer, psId);
            let result = this.filtered(atlasId, psId);
            let midPoint = getPointByEdgeId(psId, false);
            if (result && midPoint !== undefined) {
                result.modelPoint = midPoint;
            }
            return result;
        }
    }

    getPickingMask() {
        return PickingMode.FACE;
    }

    /**
     * @inheritdoc
     * @param {string} modelId
     * @param {any[]} atlasId
     */
    getSelectionObject(modelId, atlasId) {
        let psId = ConvertIds.toPsId(modelId, atlasId);
        let result = this.filtered(atlasId, psId);
        if (result) {
            result.displayName = this.getDisplayName();
            result.sourceElement = this;
        }
        return result;
    }

    getDisplayName() {
        return 'Face';
    }
    getDisplayIcon() {
        return 'select_face';
    }
}

export default {
    SelectionObject,
    SelectionElement,
    EdgeElement,
    FaceElement,
    CurveElement,
    PointElement,
    DatumPlaneElement,
    DatumAxisElement,
    BodyElement,
    PartElement,
    PmiElement,
    DimensionElement
};
